import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";

import {
  EdiClaimType,
  IEdiUploadMapping,
  IPayerTaxonomyRule,
  PlaceOfServiceCategory,
} from "../../../types/ediConversionFile";
import { IProvider } from "../../../types/Provider/Provider";
import { allowedSpecialtiesForClaimType } from "./ediMappingValidation";

// Kept in sync with the upload dialog's constants (EdiConversionFilesTable). Small, stable enums —
// duplicated here so the rule editor is self-contained and the critical upload path is untouched.
export const EDI_CLAIM_TYPE_OPTIONS: { value: EdiClaimType; label: string }[] =
  [
    { value: "INSTITUTIONAL", label: "Institutional" },
    { value: "PROFESSIONAL_ANESTHESIA", label: "Professional — Anesthesia" },
    { value: "PROFESSIONAL_PROVIDER", label: "Professional — Provider" },
  ];

const FACILITY_POS_CATEGORIES: PlaceOfServiceCategory[] = [
  "Inpatient",
  "Outpatient Facility",
];
const NON_FACILITY_POS_CATEGORIES: PlaceOfServiceCategory[] = ["Non-Facility"];

// Only the Professional — Provider rule gets a place-of-service + taxonomy sub-filter.
export const posFilterAppliesTo = (t: EdiClaimType) =>
  t === "PROFESSIONAL_PROVIDER";

const labelStyle = {
  display: "block",
  fontWeight: 600,
  marginBottom: "0.5rem",
} as const;

interface IMappingRuleFieldsProps {
  mapping: IEdiUploadMapping;
  onChange: (next: IEdiUploadMapping) => void;
  providers: IProvider[];
  disabled?: boolean;
  // Claim types that cannot be picked for this rule (already used by another rule; only
  // PROFESSIONAL_PROVIDER may repeat — CLE-2424).
  disabledClaimTypes?: EdiClaimType[];
  // Prefix for the checkbox input ids, so several editors can share a page.
  idPrefix?: string;
  // The rendering-taxonomy sub-filter (excludeMidlevels + per-payer rules). Off where the caller
  // shows those settings read-only instead (Integrations → SFTP partner form).
  showTaxonomyFilter?: boolean;
  payerOptions?: { value: number; label: string }[];
  taxonomyOptions?: { value: string; label: string }[];
  providerLabel?: (p: IProvider) => string;
  // Keep the currently selected provider in the list even when its specialty doesn't fit the claim
  // type, so a stored mismatch is visible (and flagged by the caller) rather than shown as blank.
  keepSelectedProvider?: boolean;
}

// One provider-mapping rule: claim type → provider, plus place of service (and optionally the
// taxonomy filter) for the Professional — Provider rule. Shared by the per-file Edit mapping dialog
// (CLE-2479) and the SFTP partner form (Integrations → SFTP).
function MappingRuleFields({
  mapping,
  onChange,
  providers,
  disabled = false,
  disabledClaimTypes = [],
  idPrefix = "edit",
  showTaxonomyFilter = true,
  payerOptions = [],
  taxonomyOptions = [],
  providerLabel = (p) => p.name,
  keepSelectedProvider = false,
}: IMappingRuleFieldsProps) {
  const patch = (changes: Partial<IEdiUploadMapping>) =>
    onChange({ ...mapping, ...changes });

  const typeOptions = EDI_CLAIM_TYPE_OPTIONS.map((o) => ({
    ...o,
    disabled:
      o.value !== "PROFESSIONAL_PROVIDER" &&
      disabledClaimTypes.includes(o.value),
  }));

  const allowed = allowedSpecialtiesForClaimType(mapping.claimType);
  const providerOpts = providers
    .filter(
      (p) =>
        allowed.includes(p.specialty) ||
        (keepSelectedProvider && p.id === mapping.providerId)
    )
    .map((p) => ({ label: providerLabel(p), value: p.id }));

  const showPos = posFilterAppliesTo(mapping.claimType);
  const posFilter = mapping.placeOfServiceFilter ?? [];
  const taxRules = mapping.payerTaxonomyRules ?? [];
  const setTaxRules = (rules: IPayerTaxonomyRule[]) =>
    patch({ payerTaxonomyRules: rules });

  return (
    <>
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          alignItems: "flex-start",
          marginBottom: "0.5rem",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor={`${idPrefix}-claim-type`} style={labelStyle}>
            Claim type
          </label>
          <Dropdown
            inputId={`${idPrefix}-claim-type`}
            value={mapping.claimType}
            options={typeOptions}
            optionDisabled="disabled"
            onChange={(e) =>
              onChange({
                claimType: e.value as EdiClaimType,
                providerId: "",
                placeOfServiceFilter: [],
              })
            }
            className="w-full"
            disabled={disabled}
            appendTo={document.body}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <label htmlFor={`${idPrefix}-provider`} style={labelStyle}>
            Provider
          </label>
          <Dropdown
            inputId={`${idPrefix}-provider`}
            value={mapping.providerId}
            options={providerOpts}
            onChange={(e) => patch({ providerId: e.value ?? "" })}
            placeholder="Select provider"
            className="w-full"
            filter
            showClear
            disabled={disabled}
            appendTo={document.body}
          />
        </div>
      </div>

      {showPos && (
        <div style={{ marginTop: "0.5rem" }}>
          <small
            className="text-muted"
            style={{ display: "block", marginBottom: "0.25rem" }}
          >
            Place of service (leave both unchecked to keep all):
          </small>
          <div style={{ display: "flex", gap: "1rem" }}>
            {[
              {
                key: "facility",
                label: "Facility",
                cats: FACILITY_POS_CATEGORIES,
              },
              {
                key: "non-facility",
                label: "Non-Facility",
                cats: NON_FACILITY_POS_CATEGORIES,
              },
            ].map((opt) => {
              const checked = opt.cats.some((c) => posFilter.includes(c));
              return (
                <div
                  key={opt.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                  }}
                >
                  <Checkbox
                    inputId={`${idPrefix}-pos-${opt.key}`}
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => {
                      const without = posFilter.filter(
                        (c) => !opt.cats.includes(c)
                      );
                      patch({
                        placeOfServiceFilter: e.checked
                          ? [...without, ...opt.cats]
                          : without,
                      });
                    }}
                  />
                  <label htmlFor={`${idPrefix}-pos-${opt.key}`}>
                    {opt.label}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showPos && showTaxonomyFilter && (
        <div
          style={{
            marginTop: "0.75rem",
            paddingTop: "0.75rem",
            borderTop: "1px solid var(--surface-200, #e5e7eb)",
          }}
        >
          <small
            className="text-muted"
            style={{ display: "block", marginBottom: "0.5rem" }}
          >
            Specialty (taxonomy) filter — optional. Leave unconfigured to price
            every professional claim.
          </small>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              marginBottom: "0.5rem",
            }}
          >
            <Checkbox
              inputId={`${idPrefix}-exclude-midlevels`}
              checked={mapping.excludeMidlevels ?? false}
              disabled={disabled}
              onChange={(e) => patch({ excludeMidlevels: e.checked ?? false })}
            />
            <label htmlFor={`${idPrefix}-exclude-midlevels`}>
              Exclude mid-levels (NP / PA / Audiology / LCSW)
            </label>
          </div>

          {taxRules.map((rule, ruleIdx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={ruleIdx}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.5rem",
                padding: "0.5rem",
                border: "1px solid var(--surface-200, #e5e7eb)",
                borderRadius: "4px",
              }}
            >
              <MultiSelect
                value={rule.payerIds}
                options={payerOptions}
                disabled={disabled}
                filter
                display="chip"
                placeholder="Select payer(s)"
                style={{ minWidth: "14rem" }}
                onChange={(e) => {
                  const next = [...taxRules];
                  next[ruleIdx] = { ...rule, payerIds: e.value as number[] };
                  setTaxRules(next);
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
              >
                <Checkbox
                  inputId={`${idPrefix}-taxonomy-include-all-${ruleIdx}`}
                  checked={rule.includeAll}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = [...taxRules];
                    next[ruleIdx] = {
                      ...rule,
                      includeAll: e.checked ?? false,
                      taxonomies: e.checked ? [] : rule.taxonomies,
                    };
                    setTaxRules(next);
                  }}
                />
                <label htmlFor={`${idPrefix}-taxonomy-include-all-${ruleIdx}`}>
                  Include all
                </label>
              </div>
              <MultiSelect
                value={rule.taxonomies}
                options={taxonomyOptions}
                disabled={disabled || rule.includeAll}
                filter
                display="chip"
                placeholder="Include taxonomies"
                style={{ minWidth: "16rem" }}
                onChange={(e) => {
                  const next = [...taxRules];
                  next[ruleIdx] = { ...rule, taxonomies: e.value as string[] };
                  setTaxRules(next);
                }}
              />
              <Button
                type="button"
                icon="pi pi-trash"
                className="p-button-text p-button-danger p-button-sm"
                disabled={disabled}
                aria-label="Remove taxonomy rule"
                onClick={() =>
                  setTaxRules(taxRules.filter((_, i) => i !== ruleIdx))
                }
              />
            </div>
          ))}

          <Button
            type="button"
            label="Add payer taxonomy rule"
            icon="pi pi-plus"
            className="p-button-sm p-button-text"
            disabled={disabled}
            onClick={() =>
              setTaxRules([
                ...taxRules,
                { payerIds: [], includeAll: false, taxonomies: [] },
              ])
            }
          />
        </div>
      )}
    </>
  );
}

export default MappingRuleFields;
